import { z } from 'zod';

// TypeScript interfaces for GCC landing page content
export interface CallToAction {
  text: string;
  href: string;
  variant: 'primary' | 'secondary' | 'outline';
  external?: boolean;
}

export interface HeroSection {
  headline: string;
  subtext: string;
  primaryCTA: CallToAction;
  secondaryCTA: CallToAction;
  backgroundImage?: string;
}

export interface CoreFeature {
  id: string;
  icon: string; // emoji or icon identifier
  title: string;
  description: string;
  details: string[];
}

export interface TargetAudience {
  id: string;
  title: string;
  description: string;
  icon?: string;
}

export interface ContentSection {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  items?: string[];
}

export interface GCCLandingPageData {
  hero: HeroSection;
  whySection: ContentSection;
  coreFeatures: CoreFeature[];
  targetAudiences: TargetAudience[];
  differentiators: ContentSection;
  joinSection: {
    title: string;
    subtitle: string;
    primaryCTA: CallToAction;
    secondaryCTA: CallToAction;
  };
}

// Zod validation schemas
export const CallToActionSchema = z.object({
  text: z.string().max(30),
  href: z.string(),
  variant: z.enum(['primary', 'secondary', 'outline']),
  external: z.boolean().optional()
});

export const CoreFeatureSchema = z.object({
  id: z.string(),
  icon: z.string(),
  title: z.string().max(50),
  description: z.string().max(150),
  details: z.array(z.string()).max(3)
});

export const GCCLandingPageDataSchema = z.object({
  hero: z.object({
    headline: z.string().max(100),
    subtext: z.string().max(300),
    primaryCTA: CallToActionSchema,
    secondaryCTA: CallToActionSchema,
    backgroundImage: z.string().optional()
  }),
  whySection: z.object({
    id: z.string(),
    title: z.string().max(60),
    content: z.string().max(500),
    items: z.array(z.string()).optional()
  }),
  coreFeatures: z.array(CoreFeatureSchema).length(4),
  targetAudiences: z.array(z.object({
    id: z.string(),
    title: z.string().max(30),
    description: z.string().max(200),
    icon: z.string().optional()
  })).length(4),
  differentiators: z.object({
    id: z.string(),
    title: z.string().max(60),
    content: z.string().max(500),
    items: z.array(z.string()).optional()
  }),
  joinSection: z.object({
    title: z.string(),
    subtitle: z.string(),
    primaryCTA: CallToActionSchema,
    secondaryCTA: CallToActionSchema
  })
});

// GCC Landing Page Content Data
export const gccLandingPageData: GCCLandingPageData = {
  hero: {
    headline: "Reimagining GCCs in the Age of AI",
    subtext: "The world's first integrated platform for Agent Marketplace, Thought Leadership, Benchmarks, and Provider Ecosystem — built to empower enterprises and Global Capability Centers (GCCs) to lead with AI.",
    primaryCTA: {
      text: "Get Started",
      href: "/get-started",
      variant: "primary"
    },
    secondaryCTA: {
      text: "Join Today",
      href: "/get-started", 
      variant: "secondary"
    }
  },

  whySection: {
    id: "why-platform",
    title: "Why This Platform?",
    content: "GCCs are enterprise value multipliers in the AI era. Your unique combination of process expertise, scale, and agility positions you to deliver transformational outcomes. Our platform provides the AI agents, tools, thought leadership, and ecosystem partnerships to maximize your value creation potential."
  },

  coreFeatures: [
    {
      id: "agent-marketplace",
      icon: "Bot",
      title: "Agent Marketplace",
      description: "Ready-to-deploy AI Agents designed for GCC use cases.",
      details: [
        "Ready-to-deploy AI Agents designed for GCC use cases",
        "From compliance to finance automation to vendor reconciliation"
      ]
    },
    {
      id: "tools",
      icon: "BarChart3",
      title: "Tools",
      description: "Self-assessment, templates and kits to accelerate execution.",
      details: [
        "AI‑First GCC Self Assessment",
        "AI Charter, Skills Taxonomy, Workflow Kit, Business Case"
      ]
    },
    {
      id: "thought-leadership",
      icon: "Brain",
      title: "Thought Leadership Hub",
      description: "Research reports, playbooks, and maturity frameworks.",
      details: [
        "Research reports, playbooks, and maturity frameworks for GCCs",
        "Exclusive CXO perspectives on AI + GCCs"
      ]
    },
    {
      id: "provider-ecosystem",
      icon: "Globe",
      title: "Provider Listings & Ecosystem",
      description: "Discover service providers, startups, and technology partners.",
      details: [
        "Discover service providers, startups, and technology partners",
        "Rich profiles, case studies, and solution showcases"
      ]
    }
  ],

  targetAudiences: [
    {
      id: "gcc-leaders",
      title: "GCC Leaders", 
      description: "Shape strategy and measure maturity.",
      icon: "Users"
    },
    {
      id: "enterprise-cxos",
      title: "Enterprise CXOs",
      description: "Discover how GCCs can deliver AI-led value.",
      icon: "Briefcase"
    },
    {
      id: "providers-startups", 
      title: "Providers & Startups",
      description: "Showcase capabilities and connect with global enterprises.",
      icon: "Rocket"
    },
    {
      id: "policy-academia",
      title: "Policy Makers & Academia",
      description: "Contribute to the evolving AI-in-GCC narrative.", 
      icon: "GraduationCap"
    }
  ],

  differentiators: {
    id: "differentiators",
    title: "What Makes Us Different?",
    content: "The only platform focused on AI-led GCC transformation.",
    items: [
      "The only platform focused on AI-led GCC transformation",
      "Neutral & ecosystem-driven — not tied to one vendor", 
      "Combines knowledge → benchmarks → solutions → partners into a single journey"
    ]
  },

  joinSection: {
    title: "Join the Movement",
    subtitle: "The future of GCCs is not just digital. It's AI-first, ecosystem-powered, and globally connected.",
    primaryCTA: {
      text: "Explore the Platform",
      href: "/explore-platform",
      variant: "primary"
    },
    secondaryCTA: {
      text: "Join the Community", 
      href: "/community",
      variant: "secondary"
    }
  }
};

// Validate the data at compile time
GCCLandingPageDataSchema.parse(gccLandingPageData);